/** Trust Golf theme behavior: cart drawer, product form, variant picker, and small UI helpers. */
(() => {
  const config = JSON.parse(document.getElementById('ThemeConfig')?.textContent || '{}');
  const routes = config.routes || {};
  const strings = config.strings || {};
  const DRAWER_SECTION = 'cart-drawer';

  const postJSON = (url, body) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(async (response) => {
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || strings.cartError);
      return data;
    });

  const getCart = () => fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

  const drawer = () => document.querySelector('[data-cart-drawer]');
  const onCartPage = () => Boolean(document.querySelector('[data-cart-page]'));

  function updateCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  function renderDrawer(html) {
    const current = drawer();
    if (!current || !html) return;
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-drawer-inner]');
    const inner = current.querySelector('[data-cart-drawer-inner]');
    if (next && inner) inner.replaceWith(next);
  }

  async function refreshCart() {
    if (onCartPage()) {
      window.location.reload();
      return;
    }
    const [cart, sections] = await Promise.all([
      getCart(),
      fetch(`${routes.root.replace(/\/$/, '')}/?sections=${DRAWER_SECTION}`).then((r) => r.json()),
    ]);
    updateCount(cart.item_count);
    renderDrawer(sections[DRAWER_SECTION]);
    return cart;
  }

  function openDrawer() {
    const dialog = drawer();
    if (!dialog || dialog.open) return;
    dialog.showModal();
    document.documentElement.classList.add('scroll-lock');
  }

  function closeDrawer() {
    const dialog = drawer();
    if (!dialog?.open) return;
    dialog.close();
  }

  async function changeLine(key, quantity) {
    const container = document.querySelector(`[data-key="${CSS.escape(key)}"]`);
    container?.setAttribute('aria-busy', 'true');
    try {
      await postJSON(routes.cartChange + '.js', { id: key, quantity });
      await refreshCart();
    } catch (error) {
      container?.removeAttribute('aria-busy');
      window.alert(error.message);
    }
  }

  async function addToCart(form) {
    const button = form.querySelector('[data-add-to-cart]');
    const errorEl = form.querySelector('[data-form-error]');
    button?.setAttribute('aria-busy', 'true');
    if (errorEl) errorEl.hidden = true;

    try {
      const formData = new FormData(form);
      const response = await fetch(routes.cartAdd + '.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || strings.cartError);
      await refreshCart();
      openDrawer();
    } catch (error) {
      if (errorEl) {
        errorEl.textContent = error.message;
        errorEl.hidden = false;
      }
    } finally {
      button?.removeAttribute('aria-busy');
    }
  }

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-cart-toggle]');
    if (toggle && drawer() && !onCartPage()) {
      event.preventDefault();
      openDrawer();
      return;
    }

    if (event.target.closest('[data-cart-close]') || event.target === drawer()) {
      closeDrawer();
      return;
    }

    const step = event.target.closest('[data-quantity-step]');
    if (step) {
      const wrapper = step.closest('[data-quantity]');
      const input = wrapper.querySelector('input');
      const min = Number(input.min || 0);
      input.value = Math.max(min, Number(input.value || 0) + Number(step.dataset.quantityStep));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const remove = event.target.closest('[data-line-remove]');
    if (remove) {
      event.preventDefault();
      changeLine(remove.closest('[data-key]').dataset.key, 0);
    }
  });

  let quantityTimer;
  document.addEventListener('change', (event) => {
    const lineInput = event.target.closest('[data-line-quantity]');
    if (lineInput) {
      clearTimeout(quantityTimer);
      quantityTimer = setTimeout(() => {
        changeLine(lineInput.closest('[data-key]').dataset.key, Math.max(0, Number(lineInput.value || 0)));
      }, 350);
      return;
    }

    const localization = event.target.closest('[data-localization-form]');
    if (localization && event.target.matches('select')) {
      localization.submit();
      return;
    }

    const facets = event.target.closest('[data-facets-form]');
    if (facets) {
      clearTimeout(quantityTimer);
      quantityTimer = setTimeout(() => {
        const params = new URLSearchParams(new FormData(facets));
        for (const [key, value] of [...params.entries()]) if (value === '') params.delete(key);
        window.location.search = params.toString();
      }, 400);
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form || !drawer()) return;
    event.preventDefault();
    addToCart(form);
  });

  drawer()?.addEventListener('close', () => document.documentElement.classList.remove('scroll-lock'));

  class ProductInfo extends HTMLElement {
    connectedCallback() {
      this.data = JSON.parse(this.querySelector('[data-product-json]')?.textContent || '{"variants":[]}');
      this.form = this.querySelector('[data-product-form]');
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-option-index]')) this.onOptionChange();
      });
      this.setupZoom();
      this.setupSticky();
    }

    currentVariant() {
      const id = Number(this.form?.querySelector('[data-variant-id]')?.value);
      return this.data.variants.find((variant) => variant.id === id) || this.data.variants[0];
    }

    onOptionChange() {
      const options = [...this.currentVariant().options];
      this.querySelectorAll('[data-option-index]:checked').forEach((input) => {
        options[Number(input.dataset.optionIndex)] = input.value;
      });
      const variant = this.data.variants.find((v) => v.options.every((value, i) => value === options[i]));
      this.querySelectorAll('[data-option-selected]').forEach((el) => {
        el.textContent = `: ${options[Number(el.dataset.optionSelected)]}`;
      });
      this.applyVariant(variant);
    }

    applyVariant(variant) {
      const button = this.form?.querySelector('[data-add-to-cart]');
      const label = this.form?.querySelector('[data-add-to-cart-label]');
      const price = this.querySelector('[data-price]');

      if (!variant) {
        if (button) button.disabled = true;
        if (label) label.textContent = strings.unavailable;
        return;
      }

      const idInput = this.form?.querySelector('[data-variant-id]');
      if (idInput) {
        idInput.value = variant.id;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (button) button.disabled = !variant.available;
      if (label) label.textContent = variant.available ? strings.addToCart : strings.soldOut;
      if (price) {
        price.innerHTML = `<span class="price${variant.compare_at_price ? ' price--sale' : ''}"><span class="price__current">${variant.price}</span>${
          variant.compare_at_price ? `<s class="price__compare">${variant.compare_at_price}</s>` : ''
        }</span>`;
      }
      const stickyVariant = this.querySelector('[data-sticky-variant]');
      if (stickyVariant) stickyVariant.textContent = variant.title;

      if (variant.featured_media) {
        const media = this.querySelector(`[data-media-id="${variant.featured_media}"]`);
        if (media) {
          this.querySelectorAll('.product__media.is-active').forEach((el) => el.classList.remove('is-active'));
          media.classList.add('is-active');
          media.parentElement.scrollTo({ left: media.offsetLeft, behavior: 'smooth' });
        }
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    setupZoom() {
      const lightbox = document.querySelector('[data-lightbox]');
      if (!lightbox) return;
      const image = lightbox.querySelector('[data-lightbox-image]');
      this.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-zoom]');
        if (!trigger) return;
        image.src = trigger.dataset.zoom;
        image.alt = trigger.querySelector('img')?.alt || '';
        lightbox.showModal();
      });
      lightbox.addEventListener('click', (event) => {
        if (event.target === lightbox || event.target.closest('[data-lightbox-close]')) lightbox.close();
      });
    }

    setupSticky() {
      const bar = this.querySelector('[data-sticky-atc]');
      const target = this.querySelector('.product__buy');
      if (!bar || !target) return;
      new IntersectionObserver(([entry]) => {
        bar.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
      }).observe(target);
      bar.querySelector('[data-sticky-atc-button]').addEventListener('click', () => {
        const main = this.form?.querySelector('[data-add-to-cart]');
        if (main) main.click();
        else target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }
  if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      new IntersectionObserver((entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        fetch(this.dataset.url)
          .then((r) => r.text())
          .then((html) => {
            const next = new DOMParser().parseFromString(html, 'text/html').querySelector('product-recommendations');
            if (next?.innerHTML.trim()) this.innerHTML = next.innerHTML;
          });
      }, { rootMargin: '0px 0px 400px 0px' }).observe(this);
    }
  }
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

  document.addEventListener('toggle', (event) => {
    const el = event.target;
    if (el.matches?.('[data-dropdown]') && el.open) {
      document.querySelectorAll('[data-dropdown][open]').forEach((other) => other !== el && (other.open = false));
    }
    if (el.matches?.('[data-menu-drawer]')) {
      document.documentElement.classList.toggle('scroll-lock', el.open);
    }
  }, true);

  document.addEventListener('click', (event) => {
    document.querySelectorAll('[data-dropdown][open]').forEach((dropdown) => {
      if (!dropdown.contains(event.target)) dropdown.open = false;
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-dropdown][open], [data-menu-drawer][open]').forEach((el) => {
      el.open = false;
      el.querySelector('summary')?.focus();
    });
  });

  function setupNewsletterPopup() {
    const popup = document.querySelector('[data-newsletter-popup]');
    if (!popup) return;
    const key = 'tg-newsletter-dismissed';
    const posted = /customer_posted=true|form_type=customer/.test(window.location.search);
    let dismissed = false;
    try {
      dismissed = window.localStorage.getItem(key) === '1';
    } catch (e) {}
    const dismiss = () => {
      popup.close();
      try {
        window.localStorage.setItem(key, '1');
      } catch (e) {}
    };
    popup.addEventListener('click', (event) => {
      if (event.target === popup || event.target.closest('[data-popup-close]')) dismiss();
    });
    popup.addEventListener('cancel', dismiss);
    if (popup.hasAttribute('data-design-mode')) return;
    if (posted && popup.querySelector('.newsletter-form__message')) {
      popup.showModal();
      return;
    }
    if (dismissed) return;
    setTimeout(() => {
      if (!drawer()?.open) popup.showModal();
    }, Number(popup.dataset.delay || 5) * 1000);
  }

  function setupAccountPages() {
    const recover = document.querySelector('[data-recover-panel]');
    const login = document.querySelector('[data-login-panel]');
    if (recover && login) {
      const sync = () => {
        const showRecover = window.location.hash === '#recover' || Boolean(recover.querySelector('.form-success'));
        recover.hidden = !showRecover;
        login.hidden = showRecover;
      };
      window.addEventListener('hashchange', sync);
      sync();
    }
    document.querySelectorAll('select[data-default]').forEach((select) => {
      if (select.dataset.default) select.value = select.dataset.default;
    });
  }

  function setupDragScroll() {
    document.querySelectorAll('[data-drag-scroll]').forEach((el) => {
      if (el.dataset.dragReady) return;
      el.dataset.dragReady = 'true';
      let startX = 0;
      let startScroll = 0;
      let dragging = false;
      el.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        dragging = true;
        startX = event.clientX;
        startScroll = el.scrollLeft;
        el.setPointerCapture(event.pointerId);
        el.classList.add('is-dragging');
      });
      el.addEventListener('pointermove', (event) => {
        if (dragging) el.scrollLeft = startScroll - (event.clientX - startX);
      });
      const stop = () => {
        dragging = false;
        el.classList.remove('is-dragging');
      };
      el.addEventListener('pointerup', stop);
      el.addEventListener('pointercancel', stop);
    });
  }

  function init() {
    setupDragScroll();
    setupNewsletterPopup();
    setupAccountPages();
  }

  window.TrustTheme = { refreshCart, openDrawer, updateCount };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
